// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { injectCss } from "@insite/mobius/utilities";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import parseImageUrl from "@insite/mobius/utilities/parseImageUrl";
import React from "react";
import styled from "styled-components";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getSettingsCollection } from "@stoneage/App/Store/Selectors/SettingsSelectors";
import { getCloudinaryUrl, getNotFoundImagesSettings } from "@stoneage/XDL/Utilities";
import { ImageSettingKey } from "@stoneage/XDL/types";

interface ImagePresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<ImgProps>;
}

export type ImgProps = MobiusStyledComponentProps<
    "img",
    ImagePresentationProps & {
        /** The URL to fetch the image from. If not provided, Img renders `null`. */
        src?: string;
        /** The alternative text to display in place of the image. */
        altText?: string;
        /** Controls whether the img is lazy or eager loaded, default is eager */
        loading?: string;
        // Event handler for screen readers accessing an image modal in ProductPrimaryImage.tsx
        onKeyPress?: React.KeyboardEventHandler<HTMLImageElement>;
        /**
         * XNG-Changes
         */
        imageKey?: keyof typeof ImageSettingKey;
        // XNG-Changes: Set fallback image when image not found
        fallbackImageSrc?: string;
        // XNG-Changes: Ignore fallback images when true
        disableFallbackImage?: boolean;
    }
>;

function Img({ imageKey, fallbackImageSrc, disableFallbackImage, ...props }: ImgProps) {
    const { src, altText, loading, ...otherProps } = props;
    const { mediaRootPath, notFoundImagePath } = useAppSelector(state => {
        const { notFoundCategoryImagePath, generalNotFoundImagePath } = getNotFoundImagesSettings(state);
        const notFoundImagePath =
            imageKey === ImageSettingKey.category
                ? notFoundCategoryImagePath || generalNotFoundImagePath
                : generalNotFoundImagePath;

        return {
            mediaRootPath: getSettingsCollection(state).customSettings.cloudinaryMediaRootPath,
            notFoundImagePath: disableFallbackImage ? "" : fallbackImageSrc || notFoundImagePath,
        };
    });
    const dimensions = useQueryStringsDimensions(src);

    // XNG-Changes
    const width = dimensions.width || otherProps.width;
    const imgUrl =
        getCloudinaryUrl(dimensions.url, mediaRootPath, {
            width,
        }) || notFoundImagePath;

    if (!src) {
        return null;
    }

    return (
        <StyledImg
            {...otherProps}
            loading={loading || "lazy"}
            src={imgUrl}
            alt={altText}
            width={width}
            height={dimensions.height || otherProps.height}
        />
    );
}

const useQueryStringsDimensions = (src?: string) => {
    return React.useMemo(() => {
        const queryStrings = src && /\?.*/.exec(src);
        const noDimensions = {
            url: src,
            width: "",
            height: "",
        };

        if (queryStrings) {
            const parsedUrl = parseImageUrl(src);

            if (!parsedUrl.width || !parsedUrl.height) {
                return noDimensions;
            }
            return parsedUrl;
        }
        return noDimensions;
    }, [src]);
};

const StyledImg = styled.img<InjectableCss>`
    ${injectCss}
`;

export default Img;
